import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Col, Flex, Form, Input, Row, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { mdlOtherAgencyDetailRequest } from "../../models/service-models/otherAgency/OtherAgencyDetailRequest";
import { OtherAgencyService } from "../../services/OtherAgencyService";
import { mdlOtherAgencyCreateRequest } from "../../models/service-models/otherAgency/OtherAgencyCreateRequest";
import { mdlOtherAgencyUpdateRequest } from "../../models/service-models/otherAgency/OtherAgencyUpdateRequest";
import { HotelSelect } from "../common/HotelSelect";
import { CountrySelect } from "../common/CountrySelect";
import { CitySelect } from "../common/CitySelect";
import { nanoid } from "@reduxjs/toolkit";

export type OtherAgencyDetailProps = {
    id?: string;
    hotelId?: string;
    callback?: Function;
}


export const OtherAgencyDetail = (props: OtherAgencyDetailProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [countryId, setCountryId] = useState<string>();

    const {
        data,
        isLoading,
        isError,
        isFetched,
        refetch: getAgencyDetail
    } = useQuery(
        ["getAgencyDetail", props.id],
        () => {
            if (props.id) {
                var request: mdlOtherAgencyDetailRequest = { id: props.id };
                form.resetFields();
                return OtherAgencyService.Detail(request);
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: false
        }
    );

    let agency = data?.body;

    useEffect(() => {
        if (props.id)
            getAgencyDetail();
    }, [props.id]);

    const { mutateAsync: saveAgency, isLoading: mutateLoading, data: mutateData } = useMutation(
        (data: mdlOtherAgencyCreateRequest & mdlOtherAgencyUpdateRequest) => props.id ? OtherAgencyService.Update(data as mdlOtherAgencyUpdateRequest) : OtherAgencyService.Create(data as mdlOtherAgencyCreateRequest),
        {
            onMutate(variables) {
                variables.id = props.id!
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success && data.body?.isSuccessful) {
                    toast.success(t("common.success"));
                    form.resetFields();
                    props.callback && props.callback();
                }
                else {
                    toast.error((data.header.messages && data.header.messages?.length > 0) ?
                        ObjectHelper.getErrorMessage(data.header.messages) : data.body?.message);
                }
            }
        }
    );

    const handleCountryChange = (result: string) => {
        form.setFieldsValue({ countryId: result });
        form.setFieldsValue({ cityId: undefined });
        setCountryId(result);
    }

    const getInitialValues = () => {
        return {
            hotelIds: [props.hotelId],
            ...data?.body?.data
        }
    }

    return (
        <>

            {
                !isLoading
                &&
                ((props.id && isFetched) || !props.id)
                &&
                <Spin spinning={mutateLoading}>

                    <Form

                        form={form}
                        initialValues={getInitialValues()}
                        key={nanoid()}
                        name="agency-form"
                        layout="vertical"
                        onFinish={async (values) => await saveAgency(values)}
                        autoComplete="off"
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.hotels")}
                                    name="hotelIds"
                                    rules={[{ required: true, message: t("common.required") }]}
                                >
                                    <HotelSelect disabled={props.id ? true : false}
                                        mode="multiple" onChange={(value: string) => form.setFieldsValue({ hotelIds: value })} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.agencyName")}
                                    name="name"
                                    rules={[{ required: true, message: t("common.required") }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.mailAddress")}
                                    name="notificationMail"
                                    rules={[{ required: true, message: t("common.required") }, { type: "email", message: t("common.invalidMailAddress") }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.stopFreeMailAddress")}
                                    name="email"
                                    rules={[{ required: true, message: t("common.required") }, { type: "email", message: t("common.invalidMailAddress") }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.actionMailAddress")}
                                    name="spoMail"
                                    rules={[{ required: true, message: t("common.required") }, { type: "email", message: t("common.invalidMailAddress") }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.phone")}
                                    name="phone"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.country")}
                                    name="countryId"
                                >
                                    <CountrySelect onChange={handleCountryChange} />
                                </Form.Item>

                            </Col>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.city")}
                                    name="cityId"
                                >
                                    <CitySelect countryId={data?.body?.data.countryId ?? countryId} onChange={(value: string) => form.setFieldsValue({ cityId: value })} />
                                </Form.Item>

                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.fax")}
                                    name="fax"
                                >
                                    <Input />

                                </Form.Item>

                            </Col>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.responsiblePerson")}
                                    name="primaryContact"
                                >
                                    <Input />
                                </Form.Item>

                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.webSite")}
                                    name="webSiteUrl"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item<mdlOtherAgencyUpdateRequest | mdlOtherAgencyCreateRequest>
                                    label={t("definitions.otherAgencies.address")}
                                    name="address"
                                >
                                    <Input.TextArea rows={5} maxLength={500} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => form.setFieldsValue({ address: e.currentTarget.value })} />
                                </Form.Item>

                            </Col>
                        </Row>

                        <Form.Item>
                            <Flex align={"center"} justify="flex-end">
                                <Button type="primary" htmlType="submit">{t("common.save")}</Button>
                            </Flex>

                        </Form.Item>
                    </Form>
                </Spin>
            }
        </>
    )
}
